/**
 * ReschedulePanel.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Panel de seleção de novo horário para reagendamento.
 * Exibe mini calendário semanal e slots disponíveis por período do dia.
 *
 * Nota: a lógica de salvar (UpdateAppointment + SetAppointment) é delegada
 * ao componente pai via callback onConfirm(updatedAppointment).
 * O objeto updatedAppointment preserva TODOS os campos do agendamento original
 * e sobrescreve apenas date (nova data/hora formatada) e status ('rescheduled').
 *
 * @module ReschedulePanel
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import ReactDOM from 'react-dom';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import {
  getInitials,
  formatDuration,
  isBlockingStatus,
} from '../../utils/appointmentUtils';
import {
  parseAppointmentDate,
  isSameDay,
  isToday,
} from '../../utils/calendarUtils';
import { WEEKDAYS_SHORT, MONTHS } from '../../constants/calendarConstants';
import { DataProviderInstance } from '../../../../../api/DataProvider';
import { GetAvailableSlotsAPI } from '../../../../../api/API';
import { SendWhatsAppMessage } from '../../../../../api/WhatsappAPI';
import MessagesTemplate from '../../../../../consts/MessagesTemplate';
import { getSlotIntervalMinutes } from '../../../../../consts/CalendarConfig';
import {
  SLOT_REASON_OUTSIDE,
  SLOT_REASON_PAST,
  SLOT_REASON_BOOKED,
  isSlotPast,
  classifyUnavailableSlot,
  professionalWarning,
  isSlotSelectable,
  withApiSlotTimes,
} from '../../utils/slotAvailabilityUtils';
import { procedureLabels } from '../../utils/appointmentUtils';
import styles from './ReschedulePanel.module.css';
import RescheduleConfirmDialog from './RescheduleConfirmDialog';
import { useCanSendAgendaWhatsApp } from '../../hooks/useAgendaWhatsAppSend';

// ── Constantes ────────────────────────────────────────────────────────────────

/** Hora de início dos slots exibidos */
const SLOT_START_HOUR = 7;

/** Hora de fim dos slots exibidos */
const SLOT_END_HOUR = 20;

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Formata hora para "HH:MM".
 *
 * @param {number} hour
 * @param {number} minute
 * @returns {string}
 */
function formatTime(hour, minute) {
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

/**
 * Formata data para o formato da API: "YYYY-MM-DD HH:mm:ss"
 *
 * @param {Date} date
 * @param {number} hour
 * @param {number} minute
 * @returns {string}
 */
function formatDateForAPI(date, hour, minute) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const h = String(hour).padStart(2, '0');
  const min = String(minute).padStart(2, '0');
  return `${y}-${m}-${d} ${h}:${min}:00`;
}

/**
 * Retorna os 7 dias da semana a partir de um offset de semanas.
 *
 * @param {number} weekOffset
 * @returns {Date[]}
 */
function getWeekDates(weekOffset) {
  const today = new Date();
  const dayOfWeek = today.getDay();
  const sunday = new Date(today);
  sunday.setDate(today.getDate() - dayOfWeek + weekOffset * 7);
  sunday.setHours(0, 0, 0, 0);

  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(sunday);
    d.setDate(sunday.getDate() + i);
    return d;
  });
}

/**
 * Offset de semanas (em relação à semana de hoje) em que uma data cai,
 * para o mini calendário abrir já na semana do agendamento.
 *
 * @param {Date} date
 * @returns {number}
 */
function getWeekOffsetFor(date) {
  const thisWeekSunday = getWeekDates(0)[0];
  const sunday = new Date(date);
  sunday.setHours(0, 0, 0, 0);
  sunday.setDate(sunday.getDate() - sunday.getDay());
  return Math.round((sunday - thisWeekSunday) / (7 * 24 * 60 * 60 * 1000));
}

/** Motivo (tooltip) de um slot indisponível na classificação local, sem API. */
const LOCAL_REASON = {
  occupied: SLOT_REASON_BOOKED,
  blocked: 'Horário bloqueado na agenda',
  current: 'Horário atual deste agendamento',
};

/**
 * Classifica o status de um slot para exibição.
 *
 * @param {Date} slotDate - Data do slot
 * @param {number} hour
 * @param {number} minute
 * @param {number} duration - Duração em minutos
 * @param {Array} appointments - Todos os agendamentos
 * @param {Object|null} currentAppointment - Agendamento sendo reagendado
 * @returns {'available'|'occupied'|'blocked'|'current'}
 */
function classifySlot(slotDate, hour, minute, duration, appointments, currentAppointment) {
  const slotStart = new Date(slotDate);
  slotStart.setHours(hour, minute, 0, 0);
  const slotEnd = new Date(slotStart.getTime() + duration * 60 * 1000);

  // Verifica se é o slot atual do agendamento sendo reagendado
  if (currentAppointment) {
    const aptDate = parseAppointmentDate(currentAppointment.date);
    if (
      isSameDay(aptDate, slotDate) &&
      aptDate.getHours() === hour &&
      aptDate.getMinutes() === minute
    ) {
      return 'current';
    }
  }

  // Verifica conflitos com outros agendamentos
  const dayAppointments = appointments.filter(apt => {
    const aptDate = parseAppointmentDate(apt.date);
    return isSameDay(aptDate, slotDate);
  });

  for (const apt of dayAppointments) {
    if (!isBlockingStatus(apt.status)) continue;

    const aptStart = parseAppointmentDate(apt.date);
    const aptEnd = new Date(aptStart.getTime() + (apt.duration || 50) * 60 * 1000);

    if (slotStart < aptEnd && aptStart < slotEnd) {
      // Distingue bloqueado de ocupado
      if (apt.status === 'blocked') return 'blocked';
      return 'occupied';
    }
  }

  return 'available';
}

/**
 * Gera todos os slots de um dia.
 *
 * @param {number} intervalMinutes - Intervalo entre slots em minutos
 * @returns {Array<{hour: number, minute: number, label: string}>}
 */
function generateDaySlots(intervalMinutes) {
  const slots = [];
  for (let h = SLOT_START_HOUR; h < SLOT_END_HOUR; h++) {
    for (let m = 0; m < 60; m += intervalMinutes) {
      slots.push({ hour: h, minute: m, label: formatTime(h, m) });
    }
  }
  return slots;
}

// ── Ícone SVG ─────────────────────────────────────────────────────────────────

const XIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

// ── Componente ────────────────────────────────────────────────────────────────

/**
 * Panel de seleção de novo horário para reagendamento.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Se o panel está aberto
 * @param {Object} props.appointment - Agendamento sendo reagendado
 * @param {Array} props.appointments - Todos os agendamentos (para verificar disponibilidade)
 * @param {Function} props.onClose - Fechar panel completamente
 * @param {Function} props.onBack - Voltar para o drawer de detalhes do agendamento
 * @param {Function} props.onConfirm - (updatedAppointment: Object) => void
 *   Recebe o objeto completo do agendamento com date e status atualizados.
 *   Todos os outros campos (patient, resource, procedure, duration, price, roomId, etc.)
 *   são preservados do agendamento original.
 * @returns {React.ReactElement|null}
 */
export default function ReschedulePanel({
  isOpen,
  appointment,
  appointments = [],
  onClose,
  onBack,
  onConfirm,
}) {
  const { CompanyConfig, GetPatientById } = DataProviderInstance();

  // Ref para garantir que o handleConfirm sempre usa a versão mais atual de GetPatientById
  // (evita closure stale no useCallback)
  const getPatientByIdRef = React.useRef(GetPatientById);
  React.useEffect(() => { getPatientByIdRef.current = GetPatientById; }, [GetPatientById]);
  const companyConfigRef = React.useRef(CompanyConfig);
  React.useEffect(() => { companyConfigRef.current = CompanyConfig; }, [CompanyConfig]);

  // Envio pelo sistema (Profissional ou superior / número próprio): o aviso sai
  // pelo backend depois que o pai cria o novo agendamento, sem abrir o WhatsApp Web.
  const canSendViaSystem = useCanSendAgendaWhatsApp();

  const [weekOffset, setWeekOffset] = useState(0);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // ── Estado do dialog de confirmação ────────────────────────────────────────
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // ── Nome do profissional (memoizado para uso no handleConfirm) ──────────────
  // Calculado aqui (antes do early return) para estar disponível no useCallback
  const resourceLabelMemo = useMemo(() => {
    if (!appointment?.resource || !CompanyConfig?.Resources) return '';
    const found = CompanyConfig.Resources.find(
      r => String(r.Id) === String(appointment.resource)
    );
    return found?.Label || '';
  }, [appointment?.resource, CompanyConfig?.Resources]);

  // ── Estado dos slots da API ─────────────────────────────────────────────────
  const [apiSlots, setApiSlots] = useState(null);   // null = não carregado ainda
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [slotsError, setSlotsError] = useState(null);

  // ── Abre no dia do agendamento original (não em hoje) ──────────────────────

  const appointmentDateStr = appointment?.date || null;
  useEffect(() => {
    if (!isOpen || !appointmentDateStr) return;
    const aptDay = parseAppointmentDate(appointmentDateStr);
    aptDay.setHours(0, 0, 0, 0);
    setWeekOffset(getWeekOffsetFor(aptDay));
    setSelectedDate(aptDay);
    setSelectedSlot(null);
  }, [isOpen, appointmentDateStr]);

  // ── Semana atual ────────────────────────────────────────────────────────────

  const weekDates = useMemo(() => getWeekDates(weekOffset), [weekOffset]);

  // ── Dia selecionado (padrão: dia do agendamento; senão hoje ou primeiro dia da semana)

  const activeDateObj = useMemo(
    () => selectedDate || weekDates.find(d => isToday(d)) || weekDates[0],
    [selectedDate, weekDates]
  );

  // ── Chave de data para o useEffect (evita re-render por referência de objeto) ─

  const activeDateKey = activeDateObj
    ? `${activeDateObj.getFullYear()}-${String(activeDateObj.getMonth() + 1).padStart(2, '0')}-${String(activeDateObj.getDate()).padStart(2, '0')}`
    : null;

  // ── Buscar slots da API ao mudar de dia ─────────────────────────────────────

  useEffect(() => {
    if (!activeDateKey || !appointment?.resource) {
      setApiSlots(null);
      return;
    }


    setLoadingSlots(true);
    setSlotsError(null);
    setApiSlots(null);

    GetAvailableSlotsAPI(appointment.resource, activeDateKey, (response) => {
      setLoadingSlots(false);

      if (response?.error) {
        setSlotsError(response.error);
        setApiSlots(null);
        return;
      }

      if (response?.success && Array.isArray(response.slots)) {
        setApiSlots(response.slots); // [{time: "HH:MM", available: bool}]
      } else {
        setApiSlots(null);
      }
    });
  }, [activeDateKey, appointment?.resource]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Slots do dia selecionado ────────────────────────────────────────────────

  const slotIntervalMinutes = getSlotIntervalMinutes(CompanyConfig?.TimeInterval);
  // Grade da clínica + horários do intervalo próprio do profissional (vindos da API)
  const allSlots = useMemo(
    () => withApiSlotTimes(generateDaySlots(slotIntervalMinutes), apiSlots),
    [slotIntervalMinutes, apiSlots]
  );

  const blockedTimes = CompanyConfig?.BlockedTimes;
  const endHour = Array.isArray(CompanyConfig?.AvailableTime) ? Number(CompanyConfig.AvailableTime[1]) : null;

  const classifiedSlots = useMemo(() => {
    if (!activeDateObj) return [];
    const duration = appointment?.duration || 50;

    return allSlots.map(slot => {
      // Verifica se é o slot atual do agendamento sendo reagendado (prioridade máxima)
      if (appointment) {
        const aptDate = parseAppointmentDate(appointment.date);
        if (
          isSameDay(aptDate, activeDateObj) &&
          aptDate.getHours() === slot.hour &&
          aptDate.getMinutes() === slot.minute
        ) {
          return { ...slot, status: 'current', reason: LOCAL_REASON.current };
        }
      }

      // Hora que já passou: cinza, diferente de ocupado
      if (isSlotPast(activeDateObj, slot.hour, slot.minute)) {
        return { ...slot, status: 'past', reason: SLOT_REASON_PAST };
      }

      // Se temos dados da API, usamos para determinar disponibilidade
      if (apiSlots !== null) {
        const apiSlot = apiSlots.find(s => s.time === slot.label);

        if (!apiSlot) {
          // Slot não retornado pela API = fora do horário de trabalho
          return { ...slot, status: 'blocked', reason: SLOT_REASON_OUTSIDE };
        }
        if (apiSlot.available) return { ...slot, status: 'available' };
        // Fora do horário ou no limite do profissional: escolhe, mas com aviso
        const warning = professionalWarning(apiSlot);
        if (warning) return { ...slot, ...warning };

        return {
          ...slot,
          ...classifyUnavailableSlot(slot, {
            date: activeDateObj,
            intervalMinutes: slotIntervalMinutes,
            endHour: Number.isNaN(endHour) ? null : endHour,
            blockedTimes,
          }),
        };
      }

      // Fallback: classificação local (sem API)
      const status = classifySlot(
        activeDateObj, slot.hour, slot.minute,
        duration, appointments, appointment
      );
      return { ...slot, status, reason: LOCAL_REASON[status] };
    });
  }, [activeDateObj, allSlots, apiSlots, appointments, appointment, slotIntervalMinutes, endHour, blockedTimes]);

  // Separa por período
  const morningSlots = classifiedSlots.filter(s => s.hour < 12);
  const afternoonSlots = classifiedSlots.filter(s => s.hour >= 12 && s.hour < 18);
  const eveningSlots = classifiedSlots.filter(s => s.hour >= 18);
  const hasWarningSlots = classifiedSlots.some(s => s.status === 'warning');

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleDaySelect = useCallback((date) => {
    setSelectedDate(date);
    setSelectedSlot(null);
    setApiSlots(null);
  }, []);

  const handleSlotSelect = useCallback((slot) => {
    if (!isSlotSelectable(slot)) return;
    setSelectedSlot(slot);
  }, []);

  // ── Abre o dialog de confirmação ────────────────────────────────────────────
  const handleOpenConfirmDialog = useCallback(() => {
    if (!selectedSlot || !activeDateObj || !appointment) return;
    setShowConfirmDialog(true);
  }, [selectedSlot, activeDateObj, appointment]);

  // ── Chamado pelo dialog ao confirmar (recebe sendWhatsApp do toggle do dialog) ─
  const handleConfirm = useCallback((sendWhatsapp) => {
    if (!selectedSlot || !activeDateObj || !appointment) return;
    setIsLoading(true);
    setShowConfirmDialog(false);

    // Monta a data formatada no padrão da API: "YYYY-MM-DD HH:mm:ss"
    const newDateStr = formatDateForAPI(activeDateObj, selectedSlot.hour, selectedSlot.minute);

    // Usa refs para garantir valores atuais (evita closure stale no useCallback)
    const currentGetPatientById = getPatientByIdRef.current;
    const currentCompanyConfig  = companyConfigRef.current;
    const patient = appointment.patient ? currentGetPatientById(appointment.patient) : null;
    const phone = patient?.phone;

    // Pelo sistema: o pai avisa o paciente com o id do NOVO agendamento (onConfirm options).
    const notifyViaSystem = !!(sendWhatsapp && phone && canSendViaSystem);

    // ── Abre o WhatsApp IMEDIATAMENTE no clique (antes do callback da API) ──────
    // window.open só é permitido pelo browser em resposta direta a evento de usuário.
    // Fazer isso dentro de um callback assíncrono (resposta de API) causa bloqueio de popup.
    if (sendWhatsapp && !notifyViaSystem) {
      if (phone) {
        // Tenta montar mensagem com template de reagendamento
        const templates = MessagesTemplate.getTemplates(currentCompanyConfig);
        let message = templates.AppointmentRescheduledConfirmedMessage || '';

        if (message) {
          // Extrai componentes da nova data para montar a mensagem
          const day        = String(activeDateObj.getDate()).padStart(2, '0');
          const monthName  = MONTHS[activeDateObj.getMonth()];
          const weekdayNames = ['Domingo','Segunda-feira','Terça-feira','Quarta-feira','Quinta-feira','Sexta-feira','Sábado'];
          const dayOfWeek  = weekdayNames[activeDateObj.getDay()];
          const time       = formatTime(selectedSlot.hour, selectedSlot.minute);
          const doctorName = resourceLabelMemo || '';

          // Substitui placeholders — mesmo padrão do AppointmentPopupContent
          message = message.replace('{name}',         (patient.name || '').split(' ')[0]);
          message = message.replace('{day}',          day);
          message = message.replace('{month}',        monthName);
          message = message.replace('{when}',         dayOfWeek);
          message = message.replace('{time}',         time);
          message = message.replace('{doctor_name}',  doctorName);
          message = message.replace('{company_name}', currentCompanyConfig?.Name || '');

          SendWhatsAppMessage(phone, MessagesTemplate.ConvertToWhatsAppText(message), patient?.countryCode);
        } else {
          // Template não configurado — abre WhatsApp sem mensagem pré-preenchida
          SendWhatsAppMessage(phone, '', patient?.countryCode);
        }
      }
    }

    // Monta o objeto completo do agendamento preservando TODOS os campos originais
    // e sobrescrevendo apenas a data/hora e o status.
    const updatedAppointment = {
      ...appointment,          // preserva: patient, resource, procedure, duration, price, roomId, notes, etc.
      date: newDateStr,        // sobrescreve apenas a data/hora
      status: 'rescheduled',   // marca como reagendado
    };

    // Passa o objeto completo ao pai — o pai decide como salvar (UpdateAppointment, SetAppointment, etc.)
    onConfirm?.(updatedAppointment, { notifyViaSystem });
    setIsLoading(false);
  }, [selectedSlot, activeDateObj, appointment, onConfirm, resourceLabelMemo, canSendViaSystem]);

  if (!isOpen || !appointment) return null;

  // ── Resolve nomes (mesmo padrão do AppointmentDetailPanel) ──────────────────
  const patientObj = appointment.patient ? GetPatientById(appointment.patient) : null;
  const patientName = patientObj?.name || patientObj?.Name || String(appointment.patient || '');

  // Vários procedimentos: "A + B + C"; sem linha gravada cai no principal
  const procedureLabel = procedureLabels(appointment, CompanyConfig?.Procedures) || null;

  const resourceObj = (CompanyConfig?.Resources || []).find(
    r => String(r.Id) === String(appointment.resource)
  );
  const resourceLabel = resourceObj?.Label || null;

  const initials = getInitials(patientName);
  const duration = appointment.duration || 50;

  // Subtítulo: "22/05 às 09:00 (50 min)"
  const aptDate = parseAppointmentDate(appointment.date);
  const aptDay = String(aptDate.getDate()).padStart(2, '0');
  const aptMonth = String(aptDate.getMonth() + 1).padStart(2, '0');
  const aptHour = String(aptDate.getHours()).padStart(2, '0');
  const aptMin = String(aptDate.getMinutes()).padStart(2, '0');
  const subtitle = `${aptDay}/${aptMonth} às ${aptHour}:${aptMin} (${formatDuration(duration)})`;

  // Subtítulo do header: procedimento • profissional
  const headerSubtitleParts = [procedureLabel, resourceLabel].filter(Boolean);
  const headerSubtitle = headerSubtitleParts.length > 0 ? headerSubtitleParts.join(' • ') : null;

  // Label do dia selecionado
  const activeDayLabel = activeDateObj
    ? `${['Domingo','Segunda-feira','Terça-feira','Quarta-feira','Quinta-feira','Sexta-feira','Sábado'][activeDateObj.getDay()]}, ${activeDateObj.getDate()} de ${MONTHS[activeDateObj.getMonth()]}`
    : '';

  // Label do slot selecionado para o footer
  const selectedSlotLabel = selectedSlot && activeDateObj
    ? `${String(activeDateObj.getDate()).padStart(2,'0')}/${String(activeDateObj.getMonth()+1).padStart(2,'0')} às ${selectedSlot.label}`
    : null;

  // Handler do botão fechar: volta para o drawer de detalhes se onBack estiver disponível,
  // caso contrário fecha completamente via onClose.
  const handleBack = onBack || onClose;

  // ── Renderiza grid de slots (reutilizado para cada período) ──────────────
  const SLOT_CLASS = {
    available: styles.slotAvailable,
    warning: styles.slotWarning,
    occupied: styles.slotOccupied,
    past: styles.slotPast,
    blocked: styles.slotBlocked,
    current: styles.slotCurrent,
  };

  const renderSlotGrid = (slots) => (
    <div className={styles.slotsGrid}>
      {slots.map(slot => {
        const isSelected = selectedSlot?.hour === slot.hour && selectedSlot?.minute === slot.minute;
        const slotClass = isSelected ? styles.slotSelected : SLOT_CLASS[slot.status];
        const isUnavailable = !isSlotSelectable(slot);

        // aria-disabled em vez de disabled: o navegador não mostra o title
        // (motivo) em botão desabilitado. O clique já é barrado no handleSlotSelect.
        return (
          <button
            key={slot.label}
            type="button"
            className={`${styles.slotBtn} ${slotClass}`}
            onClick={() => handleSlotSelect(slot)}
            aria-disabled={isUnavailable}
            title={slot.reason}
            aria-label={`${slot.label} — ${slot.reason || (isUnavailable ? 'indisponível' : 'disponível')}`}
            aria-pressed={isSelected}
          >
            {slot.label}
            {slot.status === 'current' && <CloseIcon className={styles.slotCurrentIcon} />}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className={styles.panelContainer} role="dialog" aria-modal="true" aria-label="Reagendar agendamento">

      {/* ── Linha do título (igual ao DrawerHeader do AppointmentDrawer) ──────── */}
      <div className={styles.drawerHeaderTop}>
        <h2 className={styles.drawerTitle}>Reagendar</h2>
        <button
          className={styles.drawerCloseBtn}
          onClick={handleBack}
          aria-label="Voltar para detalhes"
          title="Voltar para detalhes"
          type="button"
        >
          <XIcon />
        </button>
      </div>
      <div className={styles.drawerHeaderDivider} />

      {/* ── Header do paciente (igual ao AppointmentDetailPanel) ─────────────── */}
      <div className={styles.panelHeader}>
        <div className={styles.patientAvatar} aria-hidden="true">
          {initials}
        </div>
        <div className={styles.patientInfo}>
          <h2 className={styles.patientName}>
            {patientName}
          </h2>
          {headerSubtitle && (
            <span className={styles.patientSubtitle}>{headerSubtitle}</span>
          )}
        </div>
      </div>

      {/* ── Corpo ───────────────────────────────────────────────────────────── */}
      <div className={styles.panelBody}>

        {/* ── Navegação semanal ────────────────────────────────────────────── */}
        <div className={styles.weekNav}>
          <button
            type="button"
            className={styles.weekNavBtn}
            onClick={() => setWeekOffset(prev => prev - 1)}
            aria-label="Semana anterior"
          >
            ‹
          </button>

          <div className={styles.weekDays}>
            {weekDates.map((date, i) => {
              const isSelected = activeDateObj && isSameDay(date, activeDateObj);
              const isTodayDate = isToday(date);
              return (
                <button
                  key={i}
                  type="button"
                  className={`${styles.weekDay} ${isSelected ? styles.weekDaySelected : ''} ${isTodayDate && !isSelected ? styles.weekDayToday : ''}`}
                  onClick={() => handleDaySelect(date)}
                  aria-label={`${date.getDate()} de ${MONTHS[date.getMonth()]}`}
                  aria-pressed={isSelected}
                >
                  <span className={styles.weekDayLabel}>
                    {WEEKDAYS_SHORT[date.getDay()]}
                  </span>
                  <span className={styles.weekDayNumber}>
                    {date.getDate()}
                  </span>
                </button>
              );
            })}
          </div>

          <button
            type="button"
            className={styles.weekNavBtn}
            onClick={() => setWeekOffset(prev => prev + 1)}
            aria-label="Próxima semana"
          >
            ›
          </button>
        </div>

        {/* ── Título do dia ────────────────────────────────────────────────── */}
        <div className={styles.selectedDayTitle}>{activeDayLabel}</div>

        {/* ── Feedback da API ──────────────────────────────────────────────── */}
        {loadingSlots && (
          <div className={styles.slotsLoading}>
            <span className={styles.spinner} />
            <span>Verificando disponibilidade...</span>
          </div>
        )}
        {!loadingSlots && slotsError && (
          <div className={styles.slotsError}>
            <WarningAmberOutlinedIcon className={styles.slotsErrorIcon} />
            {slotsError} — exibindo disponibilidade local.
          </div>
        )}

        {/* ── Slots ────────────────────────────────────────────────────────── */}
        <div className={`${styles.slotsSection}${loadingSlots ? ` ${styles.slotsSectionLoading}` : ''}`} aria-busy={loadingSlots}>

          {/* Manhã */}
          {morningSlots.length > 0 && (
            <>
              <div className={styles.slotsPeriodLabel}>Manhã</div>
              {renderSlotGrid(morningSlots)}
            </>
          )}

          {/* Tarde */}
          {afternoonSlots.length > 0 && (
            <>
              <div className={styles.slotsPeriodLabel}>Tarde</div>
              {renderSlotGrid(afternoonSlots)}
            </>
          )}

          {/* Noite */}
          {eveningSlots.length > 0 && (
            <>
              <div className={styles.slotsPeriodLabel}>Noite</div>
              {renderSlotGrid(eveningSlots)}
            </>
          )}

          {classifiedSlots.length === 0 && (
            <div className={styles.emptySlots}>
              Nenhum horário disponível para este dia
            </div>
          )}
        </div>

        {/* ── Legenda ──────────────────────────────────────────────────────── */}
        <div className={styles.legend}>
          <div className={styles.legendItem}>
            <span className={`${styles.legendDot} ${styles.legendDotAvailable}`} />
            Disponível
          </div>
          <div className={styles.legendItem}>
            <span className={`${styles.legendDot} ${styles.legendDotOccupied}`} />
            Ocupado
          </div>
          <div className={styles.legendItem}>
            <span className={`${styles.legendDot} ${styles.legendDotPast}`} />
            Já passou
          </div>
          <div className={styles.legendItem}>
            <span className={`${styles.legendDot} ${styles.legendDotBlocked}`} />
            Fora do expediente
          </div>
          <div className={styles.legendItem}>
            <span className={`${styles.legendDot} ${styles.legendDotCurrent}`} />
            Horário atual
          </div>
          {hasWarningSlots && (
            <div className={styles.legendItem}>
              <span className={`${styles.legendDot} ${styles.legendDotWarning}`} />
              Fora do horário ou no limite do profissional
            </div>
          )}
        </div>

      </div>

      {/* ── Footer ──────────────────────────────────────────────────────────── */}
      {selectedSlot && (
        <>
          <div className={styles.panelFooterDivider} />
          <div className={styles.panelFooter}>
            {selectedSlotLabel && (
              <div className={styles.selectedSlotInfo}>
                Novo horário:{' '}
                <span className={styles.selectedSlotHighlight}>{selectedSlotLabel}</span>
                {selectedSlot.status === 'warning' && (
                  <span className={styles.slotWarningNote}>{selectedSlot.reason}. Dá para continuar.</span>
                )}
              </div>
            )}

            <button
              type="button"
              className={styles.btnConfirm}
              onClick={handleOpenConfirmDialog}
              disabled={isLoading}
            >
              {isLoading && <span className={styles.spinner} />}
              {isLoading ? 'Reagendando...' : 'Reagendar'}
            </button>
          </div>
        </>
      )}

      {/* ── Dialog de confirmação — renderizado via portal para escapar do overflow:hidden ── */}
      {showConfirmDialog && ReactDOM.createPortal(
        <RescheduleConfirmDialog
          isOpen={showConfirmDialog}
          appointment={appointment}
          patientName={patientName}
          procedureLabel={procedureLabel}
          resourceLabel={resourceLabel}
          newDate={activeDateObj}
          newHour={selectedSlot?.hour}
          newMinute={selectedSlot?.minute}
          onConfirm={handleConfirm}
          onCancel={() => setShowConfirmDialog(false)}
        />,
        document.body
      )}
    </div>
  );
}
